import { Box, Button, Card, Stack } from '@mui/material'
import { PlayButton } from 'app/_components'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNewVideoItemCardActions } from '~/features/video/hooks/useNewVideoItemCardActions'
import { thumbnailPlaceholder } from '~/libs/constants'
import * as GA from '~/libs/ga'
import { HoverTrailerPlayer } from './HoverTrailerPlayer'
import { NewVideoItemCardProps } from './NewVideoItemCard'

type HoverOverlayCardProps = {
  video: NewVideoItemCardProps['video']
  cardRect: DOMRect | null
  hoverState: 'hidden' | 'enter' | 'exit'
  onHoverEnter: () => void
  onHoverLeave: () => void
  trailer?: Parameters<typeof HoverTrailerPlayer>[0]['trailer']
}

const HOVER_CARD_SCALE = 2

export const HoverOverlayCard = ({
  video,
  cardRect,
  hoverState,
  onHoverEnter,
  onHoverLeave,
  trailer,
}: HoverOverlayCardProps) => {
  if (!cardRect || typeof window === 'undefined') return null
  const { t } = useTranslation()
  const { onClickVideoDetail } = useNewVideoItemCardActions()

  const pathname = usePathname()
  const screen = useMemo(() => {
    if (pathname === '/') return 'stream'
    if (pathname?.startsWith('/search/categories')) return 'category_videos'
    if (pathname?.startsWith('/search/result')) return 'search_result'
    return undefined
  }, [pathname])

  useEffect(() => {
    const start = performance.now()
    return () => {
      const dwellMs = Math.round(performance.now() - start)
      if (screen === undefined || dwellMs < 500) return
      GA.sendLog({ screen, action: 'hover_card_dwell', value: String(dwellMs) })
    }
  }, [])

  // 表示幅を元のサムネイル幅から計算
  const detailWidth = cardRect.width * HOVER_CARD_SCALE
  const detailHeight = detailWidth * (126 / 224)
  const startScale = 1 / HOVER_CARD_SCALE

  // 元のサムネイル中心に合わせつつ、左右の画面端との余白を確保
  const horizontalMargin = 24
  const unclampedLeft = cardRect.left + cardRect.width / 2 - detailWidth / 2
  const unclampedTop = cardRect.top + window.scrollY + cardRect.height / 2 - detailHeight / 2

  const targetLeft = Math.min(
    Math.max(unclampedLeft, horizontalMargin),
    window.innerWidth - horizontalMargin - detailWidth,
  )
  const targetTop = unclampedTop

  return createPortal(
    <Box
      sx={{
        position: 'absolute',
        left: targetLeft,
        top: targetTop,
        width: detailWidth,
        zIndex: (theme) => theme.zIndex.fab,
        pointerEvents: 'none',
      }}
    >
      <Box
        onPointerEnter={onHoverEnter}
        onPointerLeave={onHoverLeave}
        className={hoverState === 'exit' ? 'fade-out' : undefined}
        sx={{
          pointerEvents: 'auto',
          width: '100%',
          animation: 'growIn 260ms cubic-bezier(0.3, 0.3, 0.3, 1) forwards',
          transformOrigin: 'center',
          willChange: 'transform, opacity, filter',
          '&.fade-out': {
            animation: 'growOut 180ms cubic-bezier(0, 0, 0, 0) forwards',
          },
          '@keyframes growIn': {
            from: {
              opacity: 0,
              transform: `scale(${startScale}) translateY(12px)`,
              filter: 'blur(2px)',
            },
            to: { opacity: 1, transform: 'scale(1) translateY(0)', filter: 'blur(0px)' },
          },
          '@keyframes growOut': {
            from: { opacity: 1, transform: 'scale(1) translateY(0)', filter: 'blur(0px)' },
            to: {
              opacity: 0,
              transform: `scale(${startScale}) translateY(12px)`,
              filter: 'blur(3px)',
            },
          },
        }}
      >
        <Card
          sx={{
            position: 'relative',
            width: detailWidth,
            height: detailHeight,
            borderRadius: 2,
          }}
        >
          <Image
            alt='thumbnail'
            src={video.landscapeThumbnail ?? ''}
            fill
            sizes='25vw'
            style={{
              objectFit: 'cover',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
            placeholder='blur'
            blurDataURL={thumbnailPlaceholder}
          />
          <HoverTrailerPlayer videoId={video.id} trailer={trailer} />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              pb: 3,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <Stack
              direction='row'
              spacing={1}
              justifyContent='center'
              sx={{ pointerEvents: 'auto' }}
            >
              <PlayButton videoId={video.id} />
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  onClickVideoDetail(Number(video.id))
                }}
                sx={{
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(0,0,0,0.24)',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
                  ':hover': {
                    backgroundColor: 'rgba(0,0,0,0.32)',
                  },
                }}
              >
                {t('details')}
              </Button>
            </Stack>
          </Box>
        </Card>
      </Box>
    </Box>,
    document.body,
  )
}
